// frontend/src/pages/dashboards/DashboardPage.jsx
// Phase 4 step 14a-3: one dashboard, at /dashboards/:id.
//
// View mode (everyone): the title and description, the grid of tiles, each
// chart drawn live by ChartView. Edit mode (admins and analysts, "Edit" in
// the header): add charts from the picker, add text, move and resize on the
// grid, retitle a tile, remove a tile, change the title and description, then
// Save or Discard. Nothing reaches the server until Save. The save names the
// revision the page opened; when someone else saved in between the server
// refuses it (409 DASHBOARD_CHANGED) and a banner offers to reload their
// version; the layout on screen is kept until then, so nothing typed is lost.
// Leaving the tab with unsaved changes asks first (in-app links are not
// blocked: the app uses a plain BrowserRouter, as the chart builder notes).
//
// Step 14b-2: the filter bar. Its definitions are part of the draft (added,
// changed and removed in edit mode, saved with Save); what a viewer picks is
// kept in the URL (?f=, filterState.js), never saved, so a link carries it.
// Each chart tile gets its share of the bar (filterState.requestFilters) and
// refetches narrowed. A dashboard without filters fetches exactly as before.
//
// Step 14c-3: Share (admins and analysts, in view and edit mode) opens the
// Share dialog for public links; a dashboard with a working link shows a
// "Shared publicly" tag under its title for everyone.
//
// Step 14d-3: what this person may do comes from the dashboard's
// `permissions` (accessState.pagePermissions): a member a dashboard was
// shared with view only gets no Edit, Duplicate, Delete or Share. Share opens
// the dialog with who can open it, people and links; a restricted dashboard
// shows "Only specific people" under its title, and the tag follows the
// dialog without a reload.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Copy, Loader2, Pencil, Plus, Share2, Trash2, Type } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Panel } from '@/components/ui/panel.jsx';
import ConfirmDialog from '../../components/modals/ConfirmDialog';
import { deleteDashboard, duplicateDashboard, getDashboard, getFields, saveDashboard, viewDashboard } from '../../charts/semanticApi';
import DashboardGrid from './DashboardGrid';
import DashboardTile from './DashboardTile';
import FilterBar from './FilterBar';
import ShareDialog from './ShareDialog';
import SharedTag from './SharedTag';
import RestrictedTag from './RestrictedTag';
import { pagePermissions } from './accessState';
import { FilterEditorDialog, TileFiltersDialog } from './FilterDialogs';
import { ChartPicker, TextTileDialog, TileTitleDialog } from './TileDialogs';
import {
  PARAM, datasetIds, decodeOverrides, effectiveValues, encodeOverrides, fieldUnion, hasOverrides, pruneBindings, requestFilters,
  setOverride, withBinding,
} from './filterState';
import { MAX_TILES, addTile, removeTile } from './gridLayout';
import {
  MAX_DESCRIPTION, MAX_TITLE, draftFromServer, draftProblem, isDirty, loadFailureMessage, newChartTile, newTextTile,
  saveFailureMessage, tileCountText, tileTitle, toSave, updateTile, updatedText, withTileTitle,
} from './dashboardState';

const FIELD = 'rounded-xl bg-[var(--ui-inset)] px-3 text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30';

function Loading({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-[var(--ui-text-muted)]" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {label}
    </div>
  );
}

export default function DashboardPage() {
  const { id } = useParams();
  const dashboardId = Number(id);
  const navigate = useNavigate();

  const [state, setState] = useState({ status: 'loading', dashboard: null, error: null });
  const [attempt, setAttempt] = useState(0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [save, setSave] = useState({ busy: false, error: null, conflict: null, savedAt: 0 });
  const [picker, setPicker] = useState(false);
  const [textEdit, setTextEdit] = useState(null);       // { key | null, text }
  const [titleEdit, setTitleEdit] = useState(null);     // { key, title, chartTitle }
  const [confirm, setConfirm] = useState(null);         // ConfirmDialog request
  const [acting, setActing] = useState('');             // 'duplicate' | 'delete'
  const [actionError, setActionError] = useState('');
  const [filterEdit, setFilterEdit] = useState(null);   // { def | null, current }
  const [bindingEdit, setBindingEdit] = useState(null); // { tile }
  const [fieldLists, setFieldLists] = useState({});     // dataset id -> { status, fields }
  const [sharing, setSharing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const viewed = useRef(null);

  // --- load ---------------------------------------------------------------------
  useEffect(() => {
    if (!Number.isInteger(dashboardId) || dashboardId <= 0) {
      setState({ status: 'error', dashboard: null, error: { code: 'NOT_FOUND' } });
      return undefined;
    }
    const ctrl = new AbortController();
    setState((s) => ({ ...s, status: 'loading', error: null }));
    getDashboard(dashboardId, { signal: ctrl.signal })
      .then((dashboard) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', dashboard, error: null });
        const d = draftFromServer(dashboard);
        setBaseline(d);
        setDraft(d);
        setEditing(false);
        setSave({ busy: false, error: null, conflict: null, savedAt: 0 });
        if (viewed.current !== dashboardId) {
          viewed.current = dashboardId;
          viewDashboard(dashboardId).catch(() => {});       // counting a view is never worth an error
        }
      })
      .catch((error) => { if (!ctrl.signal.aborted) setState({ status: 'error', dashboard: null, error }); });
    return () => ctrl.abort();
  }, [dashboardId, attempt]);

  const dashboard = state.dashboard;
  const rights = pagePermissions(dashboard);
  const canEdit = rights.edit;
  const canShare = rights.share;
  const dirty = Boolean(draft && baseline && isDirty(draft, baseline));
  const problem = draft ? draftProblem(draft) : null;

  // Leaving the tab with unsaved work asks first.
  useEffect(() => {
    if (!(editing && dirty)) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [editing, dirty]);

  const setTiles = useCallback((tiles) => setDraft((d) => (d && tiles !== d.tiles ? { ...d, tiles } : d)), []);

  // --- the filter bar -------------------------------------------------------------
  const defs = useMemo(() => (draft ? draft.filters : []), [draft]);
  const param = searchParams.get(PARAM) || '';
  const overrides = useMemo(() => decodeOverrides(param, defs), [param, defs]);
  const values = useMemo(() => effectiveValues(defs, overrides), [defs, overrides]);

  const writeOverrides = useCallback((next) => {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev);
      const encoded = encodeOverrides(defs, next);
      if (encoded) out.set(PARAM, encoded); else out.delete(PARAM);
      return out;
    }, { replace: true });
  }, [defs, setSearchParams]);

  const setFilterValue = useCallback((id, value) => writeOverrides(setOverride(defs, overrides, id, value)), [defs, overrides, writeOverrides]);
  const resetFilters = useCallback(() => writeOverrides({}), [writeOverrides]);

  // The fields of the dashboard's datasets, read when an editor dialog needs them.
  const asked = useRef(new Set());
  const loadFields = useCallback((ids) => {
    for (const id of ids) {
      if (asked.current.has(id)) continue;
      asked.current.add(id);
      setFieldLists((h) => ({ ...h, [id]: { status: 'loading', fields: [] } }));
      getFields(id)
        .then((body) => setFieldLists((h) => ({ ...h, [id]: { status: 'ready', fields: (body && body.fields) || [] } })))
        .catch(() => { asked.current.delete(id); setFieldLists((h) => ({ ...h, [id]: { status: 'error', fields: [] } })); });
    }
  }, []);

  const tileIds = draft ? datasetIds(draft.tiles) : [];
  const editorFields = useMemo(() => {
    const lists = tileIds.map((id) => fieldLists[id]).filter(Boolean);
    const status = lists.some((l) => l.status === 'loading') || lists.length < tileIds.length ? 'loading'
      : lists.some((l) => l.status === 'ready') || !tileIds.length ? 'ready' : 'error';
    return { status, list: fieldUnion(lists.map((l) => l.fields)), datasets: tileIds.length };
  }, [tileIds.join(','), fieldLists]); // eslint-disable-line react-hooks/exhaustive-deps

  const openFilterEditor = (def) => {
    loadFields(tileIds);
    setFilterEdit({ def: def || null, current: def ? values[def.id] : null });
  };

  const saveFilter = (def) => {
    setDraft((d) => {
      const i = d.filters.findIndex((f) => f.id === def.id);
      const filters = i < 0 ? [...d.filters, def] : d.filters.map((f, j) => (j === i ? def : f));
      return { ...d, filters };
    });
    setFilterEdit(null);
  };

  const removeFilter = (def) => setDraft((d) => {
    const filters = d.filters.filter((f) => f.id !== def.id);
    return { ...d, filters, tiles: pruneBindings(d.tiles, filters) };
  });

  const openBindings = (tile) => {
    if (tile.chart && tile.chart.dataset_id) loadFields([tile.chart.dataset_id]);
    setBindingEdit({ tile });
  };

  const saveBindings = (key, bindings) => {
    setDraft((d) => {
      let tiles = d.tiles;
      for (const [id, b] of Object.entries(bindings)) tiles = withBinding(tiles, key, id, b);
      return { ...d, tiles };
    });
    setBindingEdit(null);
  };

  // --- editing ------------------------------------------------------------------
  const startEditing = () => { setSave((s) => ({ ...s, error: null, conflict: null })); setEditing(true); };

  const discard = () => {
    const done = () => { setDraft(baseline); setEditing(false); setSave({ busy: false, error: null, conflict: null, savedAt: 0 }); };
    if (!dirty) { done(); return; }
    setConfirm({
      title: 'Discard changes?', message: 'Everything you changed since the last save will be lost.',
      confirmText: 'Discard', variant: 'warning', onConfirm: done,
    });
  };

  const doSave = async () => {
    if (!draft || !baseline || !dashboard || save.busy || problem) return;
    setSave((s) => ({ ...s, busy: true, error: null, conflict: null }));
    try {
      const body = toSave({ ...draft, title: draft.title.trim(), description: draft.description.trim() }, baseline, dashboard.revision);
      const saved = await saveDashboard(dashboardId, body);
      const d = draftFromServer(saved);
      setState({ status: 'ready', dashboard: saved, error: null });
      setBaseline(d);
      setDraft(d);
      setSave({ busy: false, error: null, conflict: null, savedAt: Date.now() });
    } catch (err) {
      if (err && err.code === 'DASHBOARD_CHANGED') {
        setSave({ busy: false, error: null, conflict: { revision: err.revision }, savedAt: 0 });
      } else {
        setSave({ busy: false, error: saveFailureMessage(err), conflict: null, savedAt: 0 });
      }
    }
  };

  const reloadTheirs = () => setAttempt((n) => n + 1);     // the load effect replaces draft and baseline and leaves edit mode

  // The dialog reports whether a link works after every change; the tag follows without a reload.
  const setShared = useCallback((shared) => setState((s) => (s.dashboard && s.dashboard.shared !== shared
    ? { ...s, dashboard: { ...s.dashboard, shared } } : s)), []);
  const setAccess = useCallback((access) => setState((s) => (s.dashboard && s.dashboard.access !== access
    ? { ...s, dashboard: { ...s.dashboard, access } } : s)), []);

  const addChart = (chart) => {
    const next = addTile(draft.tiles, newChartTile(chart));
    if (!next) { setSave((s) => ({ ...s, error: `A dashboard holds at most ${MAX_TILES} tiles.` })); return; }
    setTiles(next);
    setPicker(false);
  };

  const saveText = (text, key) => {
    if (key) setTiles(updateTile(draft.tiles, key, { text }));
    else {
      const next = addTile(draft.tiles, newTextTile(text));
      if (!next) { setSave((s) => ({ ...s, error: `A dashboard holds at most ${MAX_TILES} tiles.` })); return; }
      setTiles(next);
    }
    setTextEdit(null);
  };

  const saveTitle = (title, key) => { setTiles(withTileTitle(draft.tiles, key, title)); setTitleEdit(null); };

  const remove = (tile) => setTiles(removeTile(draft.tiles, tile.key));

  // --- duplicate / delete (view mode) --------------------------------------------
  const duplicate = async () => {
    setActing('duplicate'); setActionError('');
    try {
      const copy = await duplicateDashboard(dashboardId);
      navigate(`/dashboards/${copy.id}`);
    } catch (err) {
      setActionError(err && err.status === 403 ? "You don't have permission to copy dashboards." : "The dashboard couldn't be copied. Try again.");
    } finally { setActing(''); }
  };

  const askDelete = () => setConfirm({
    title: 'Delete this dashboard?', message: `"${dashboard.title}" and its tiles will be deleted. The charts themselves are kept.`,
    confirmText: 'Delete', variant: 'danger',
    onConfirm: async () => {
      setActing('delete'); setActionError('');
      try { await deleteDashboard(dashboardId); navigate('/dashboards'); } catch (err) {
        setActionError(err && err.status === 403 ? "You don't have permission to delete dashboards." : "The dashboard couldn't be deleted. Try again.");
        setActing('');
      }
    },
  });

  // --- render -------------------------------------------------------------------
  const onDashboard = useMemo(() => new Set((draft ? draft.tiles : []).map((t) => t.chartId).filter(Boolean)), [draft]);

  if (state.status === 'loading' && !dashboard) return <Loading label="Loading dashboard" />;
  if (state.status === 'error') {
    return (
      <Panel className="flex flex-col items-center gap-3 py-16 text-center">
        <p className="text-sm text-[var(--ui-text-muted)]">{loadFailureMessage(state.error)}</p>
        <div className="flex gap-3">
          <Button variant="secondary" size="sm" onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
          <Button variant="ghost" size="sm" asChild><Link to="/dashboards">All dashboards</Link></Button>
        </div>
      </Panel>
    );
  }

  const tiles = draft ? draft.tiles : [];
  const renderTile = (tile, ctx) => (
    <DashboardTile tile={tile} editing={ctx.editing} dragging={ctx.dragging}
      onEditText={(t) => setTextEdit({ key: t.key, text: t.text })}
      onEditTitle={(t) => setTitleEdit({ key: t.key, title: t.settings.title || '', chartTitle: t.chart ? t.chart.title : '' })}
      onRemove={remove}
      defs={defs} request={defs.length && tile.kind === 'chart' ? requestFilters(defs, values, tile) : null}
      onEditFilters={openBindings} />
  );
  const bindingTile = bindingEdit ? tiles.find((t) => t.key === bindingEdit.tile.key) : null;
  const bindingFields = bindingTile && bindingTile.chart && bindingTile.chart.dataset_id
    ? fieldLists[bindingTile.chart.dataset_id] || { status: 'loading', fields: [] } : { status: 'ready', fields: [] };

  return (
    <div className="flex flex-col gap-4" data-dashboard-mode={editing ? 'edit' : 'view'}>
      {/* header */}
      <Panel className="py-3.5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-x-6">
          <div className="min-w-0 lg:flex-1">
            <Link to="/dashboards" className="mb-1 inline-flex items-center gap-1 text-xs text-[var(--ui-text-muted)] hover:text-[var(--ui-text)]">
              <ArrowLeft className="h-3 w-3" aria-hidden="true" /> All dashboards
            </Link>
            {editing ? (
              <div className="flex flex-col gap-2">
                <input type="text" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} maxLength={MAX_TITLE}
                  aria-label="Dashboard title" placeholder="Dashboard title" className={`${FIELD} h-10 w-full max-w-xl text-lg font-semibold tracking-[-0.01em]`} />
                <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} maxLength={MAX_DESCRIPTION} rows={2}
                  aria-label="Description" placeholder="Description (optional)" className={`${FIELD} w-full max-w-xl resize-y py-2 text-sm`} />
              </div>
            ) : (
              <>
                <h1 className="truncate text-xl font-semibold tracking-[-0.01em] text-[var(--ui-text)]">{dashboard.title}</h1>
                {dashboard.description && <p className="mt-1 max-w-3xl whitespace-pre-wrap text-sm text-[var(--ui-text-muted)]">{dashboard.description}</p>}
                <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--ui-text-muted)]">
                  <span>{tileCountText(tiles.length)}{updatedText(dashboard.updated_at, dashboard.created_by) ? ` · ${updatedText(dashboard.updated_at, dashboard.created_by)}` : ''}</span>
                  {dashboard.access === 'restricted' && <RestrictedTag />}
                  {dashboard.shared === true && <SharedTag />}
                </p>
              </>
            )}
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {!editing && canShare && (
              <Button variant="secondary" size="sm" onClick={() => setSharing(true)}>
                <Share2 className="h-3.5 w-3.5" aria-hidden="true" /> Share
              </Button>
            )}
            {!editing && canEdit && (
              <>
                <Button variant="secondary" size="sm" onClick={duplicate} disabled={Boolean(acting)}>
                  {acting === 'duplicate' ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />} Duplicate
                </Button>
                <Button variant="secondary" size="sm" onClick={askDelete} disabled={Boolean(acting)}>
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
                </Button>
                <Button size="sm" onClick={startEditing}><Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit</Button>
              </>
            )}
            {editing && (
              <>
                <Button variant="secondary" size="sm" onClick={() => setPicker(true)} disabled={tiles.length >= MAX_TILES}>
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add chart
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setTextEdit({ key: null, text: '' })} disabled={tiles.length >= MAX_TILES}>
                  <Type className="h-3.5 w-3.5" aria-hidden="true" /> Add text
                </Button>
                {canShare && (
                  <Button variant="secondary" size="sm" onClick={() => setSharing(true)}>
                    <Share2 className="h-3.5 w-3.5" aria-hidden="true" /> Share
                  </Button>
                )}
                <span className="mx-1 h-5 w-px bg-[var(--ui-divider)]" aria-hidden="true" />
                <Button variant="ghost" size="sm" onClick={discard} disabled={save.busy}>{dirty ? 'Discard' : 'Done'}</Button>
                <Button size="sm" onClick={doSave} disabled={save.busy || !dirty || Boolean(problem)} title={problem || undefined}>
                  {save.busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />} Save
                </Button>
              </>
            )}
          </div>
        </div>

        {editing && (
          <p className="mt-2 text-xs text-[var(--ui-text-muted)]" role="status">
            {save.busy ? 'Saving…'
              : dirty ? (problem || 'Unsaved changes. Drag a tile to move it, or its corner to resize it.')
                : save.savedAt ? 'Saved.' : 'Drag a tile to move it, or its corner to resize it.'}
          </p>
        )}
        {(save.error || actionError) && <p className="mt-2 text-xs text-red-400" role="alert">{save.error || actionError}</p>}
      </Panel>

      {/* conflict banner */}
      {save.conflict && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-[22px] bg-amber-500/10 px-5 py-3 text-sm text-[var(--ui-text)] ring-1 ring-amber-500/30">
          <span>Someone else saved this dashboard after you opened it. Reload to see their version; what you changed here will be lost.</span>
          <span className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSave((s) => ({ ...s, conflict: null }))}>Keep editing</Button>
            <Button variant="secondary" size="sm" onClick={reloadTheirs}>Reload</Button>
          </span>
        </div>
      )}

      {/* the filter bar */}
      <FilterBar defs={defs} values={values} onChange={setFilterValue} onReset={resetFilters} canReset={hasOverrides(defs, overrides)}
        tiles={tiles} editing={editing} onAdd={() => openFilterEditor(null)} onEdit={openFilterEditor} onRemove={removeFilter} />

      {/* the grid */}
      {tiles.length === 0 ? (
        <Panel className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-sm text-[var(--ui-text-muted)]">
            {editing ? 'Add a chart or some text to start.' : canEdit ? 'This dashboard is empty. Edit it to add charts.' : 'This dashboard has no tiles yet.'}
          </p>
          {editing && <Button size="sm" onClick={() => setPicker(true)}><Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add chart</Button>}
          {!editing && canEdit && <Button size="sm" onClick={startEditing}><Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit</Button>}
        </Panel>
      ) : (
        <DashboardGrid tiles={tiles} editing={editing} onChange={setTiles} renderTile={renderTile} tileLabel={tileTitle} />
      )}
      {editing && tiles.length > 0 && (
        <p className="text-center text-xs text-[var(--ui-text-muted)]">On a narrow screen tiles stack top to bottom, left to right.</p>
      )}

      <ChartPicker open={picker} onClose={() => setPicker(false)} onPick={addChart} onDashboard={onDashboard} />
      <TextTileDialog request={textEdit} onClose={() => setTextEdit(null)} onSave={saveText} />
      <TileTitleDialog request={titleEdit} onClose={() => setTitleEdit(null)} onSave={saveTitle} />
      <FilterEditorDialog request={filterEdit} fields={editorFields} others={defs} onClose={() => setFilterEdit(null)} onSave={saveFilter} />
      <TileFiltersDialog request={bindingTile ? { tile: bindingTile, fields: { status: bindingFields.status, list: bindingFields.fields } } : null}
        defs={defs} onClose={() => setBindingEdit(null)} onSave={saveBindings} />
      {canShare && (
        <ShareDialog open={sharing} dashboardId={dashboardId} dirty={editing && dirty} onClose={() => setSharing(false)}
          onShared={setShared} onAccess={setAccess} />
      )}
      <ConfirmDialog
        isOpen={Boolean(confirm)} onClose={() => setConfirm(null)} onConfirm={confirm ? confirm.onConfirm : () => {}}
        title={confirm ? confirm.title : ''} message={confirm ? confirm.message : ''}
        confirmText={confirm ? confirm.confirmText : 'OK'} cancelText="Cancel" variant={confirm ? confirm.variant : 'danger'}
      />
    </div>
  );
}
